import { createApiClient } from "../../utils/apiClient";
import { getPdfServerUrl } from "../../utils/pdfServer";
import type { FormattedInvoicePayload } from "../formattedInvoice/formattedInvoiceData";
import { assertReportAdmission } from "./nativeConditions.mjs";
import type { RenderContext } from "./renderOperation";

export const NATIVE_XLSX_MIME = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

export async function exportNativeExcel({
  payload, fileName, token, getToken, tenantId, userId, sourceMode, isCurrent, renderContext,
}: {
  payload: FormattedInvoicePayload; fileName: string; token: string; getToken?: () => Promise<string>;
  tenantId: string; userId: number; sourceMode: "business" | "sample";
  isCurrent?: () => boolean;
  renderContext?: RenderContext;
}): Promise<number> {
  assertReportAdmission(payload);
  const api = createApiClient({ baseUrl: getPdfServerUrl(), token, getToken, tenantId, userId });
  const response = await api.postResponse("reports/reportsv3/excel", { payload, fileName, ...renderContext, nativeContext: { sourceMode } }, { accept: NATIVE_XLSX_MIME, headers: { "X-Reports-Client-Capabilities": "" } });
  const contentType = response.headers.get("content-type") || "";
  const bytes = new Uint8Array(await response.arrayBuffer());
  if (isCurrent && !isCurrent()) throw new DOMException("Report access changed before export completed.", "AbortError");
  if (!contentType.toLowerCase().includes(NATIVE_XLSX_MIME) || bytes.length < 4 || bytes[0] !== 0x50 || bytes[1] !== 0x4b || bytes[2] !== 3 || bytes[3] !== 4) {
    throw new Error("Expected a valid XLSX file. The export server returned an unexpected response; check the Excel route and authentication.");
  }
  const url = URL.createObjectURL(new Blob([bytes], { type: NATIVE_XLSX_MIME }));
  const link = document.createElement("a");
  try {
    link.href = url;
    link.download = fileName.toLowerCase().endsWith(".xlsx") ? fileName : `${fileName}.xlsx`;
    document.body.appendChild(link);
    link.click();
  } finally {
    link.remove();
    URL.revokeObjectURL(url);
  }
  const warnings = Number(response.headers.get("X-Reports-Export-Warning-Count") || 0);
  return Number.isSafeInteger(warnings) && warnings > 0 ? warnings : 0;
}
